import type React from "react";
import {useMemo} from "react";
import {Box, Stack, Typography} from "@mui/material";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid";
import {EMPTY_CELL} from "~/components/data-grid/cells/gridCellFormat";
import type {ComplianceRequirementGridRow} from "~/pages/supplier-360/types/Supplier360Types";
import {useComplianceBuRequirementsQuery} from "~/pages/supplier-360/tabs/SupplierCompliance/hooks/useComplianceBuGroupsQuery";
import {getComplianceBuRequirementColumns} from "~/pages/supplier-360/tabs/SupplierCompliance/components/businessUnitsTable/complianceBuRequirementColumns";

/** The design's expanded-row wash (`.sxb-expand` → `--ac-bg-row`); no theme token matches it. */
const AC_BG_ROW = 'rgb(248, 249, 251)';

type ComplianceBuRequirementsPanelProps = {
    entityId: number;
    databagId: number | undefined;
    buQualificationId: number;
    businessUnitName: string | undefined;
    onOpen: (row: ComplianceRequirementGridRow) => void;
};

/**
 * A Business units row expanded (`SxbRequirements`): that BU's own business-unit records plus the
 * company-wide ones linked to it (§2.8a, `groupKeys=[id]`). Fetched when the row is first opened.
 */
export const ComplianceBuRequirementsPanel: React.FC<ComplianceBuRequirementsPanelProps> = ({
                                                                                                entityId,
                                                                                                databagId,
                                                                                                buQualificationId,
                                                                                                businessUnitName,
                                                                                                onOpen,
                                                                                            }) => {
    const {data, isFetching, isError} = useComplianceBuRequirementsQuery(entityId, databagId, buQualificationId);
    const rows = useMemo(() => data ?? [], [data]);
    const name = businessUnitName ?? EMPTY_CELL;
    const columns = useMemo(
        () => getComplianceBuRequirementColumns({businessUnitName: name, onOpen}),
        [name, onOpen],
    );

    const inherited = rows.filter(row => row.layer === 'company_wide').length;
    const own = rows.length - inherited;

    return (
        <Box sx={{bgcolor: AC_BG_ROW, pb: 0.5}}>
            <Stack direction="row" gap={1.25} sx={{alignItems: 'baseline', px: 2, pt: 1.5, pb: 1}}>
                <Typography variant="body2" fontWeight={600}>{`Compliance requirements · ${name}`}</Typography>
                {/* Counted only once the rows are in — not "0 · 0" while they load. */}
                {data && (
                    <Typography variant="caption" color="text.secondary">
                        {`${inherited} inherited from the company layer · ${own} held at this business unit`}
                    </Typography>
                )}
            </Stack>
            {isError
                ? <Typography variant="body2" color="text.secondary">Could not load this business unit's requirements.</Typography>
                : (
                    <CustomDataGrid
                        layoutKey="supplier-360-compliance-bu-requirements"
                        rows={rows}
                        columns={columns}
                        loading={isFetching}
                        pagination={false}
                        hideFooter
                        autoHeight
                        disableRowSelectionOnClick
                        getRowHeight={() => 64}
                        onRowClick={({row}) => onOpen(row)}
                        localeText={{noRowsLabel: 'No applicable compliance requirements. Add a compliance record to make one applicable.'}}
                        sx={{bgcolor: 'background.paper', '& .MuiDataGrid-row': {cursor: 'pointer'}}}
                    />
                )}
        </Box>
    );
};
